import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    signal,
    inject,
    DestroyRef,
} from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IChatConversation, IChatConversationMessage } from '@fi-shared/fi-chat/fi-chat';
import { FiChatService } from '@fi-shared/fi-chat/fi-chat.service';
import { filter, take, takeUntil } from 'rxjs/operators';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { cloneDeep, eventFindInPath, isDefined } from '@fi-shared/utils';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { FiChatConversationMessageType } from '@fi-shared/fi-chat/fi-chat-conversation-message-type';
import { PersonalNotificationsCategoryType } from '@fi-shared/types';
import { FiEmojiService } from '@fi-shared/fi-emoji-button/fi-emoji.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiDataSourceBase, ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { FiScrollToElementAnimateService } from '@fi-shared/fi-shared-services/scroll-to-element-animate.service';
import { MobileAppHelperBaseService } from '@fi-shared/fi-shared-services/mobile-app-helper-base.service';
import { WhenScrolledService } from '@fi-shared/fi-directives/when-scrolled/when-scrolled.service';
import { FiChatSyncService } from '@fi-shared/fi-chat/fi-chat-sync.service';
import { IChatConversationIsTypingChangedEventData } from '@fi-shared/fi-signal-r/fi-signal-r';
import { FiChatSourceMessageType } from '@fi-shared/fi-chat/fi-chat-source-message-type';
import { FiChatConversationType } from '@fi-shared/fi-chat/fi-chat-conversation-type';
import {
    FiChatConversationMessageActionsService,
    IChatConversationMessageActionsData,
} from '@fi-shared/fi-chat/fi-chat-conversation/fi-chat-conversation-message-actions.service';
import { FiChatConversationHelperService } from '@fi-shared/fi-chat/fi-chat-conversation/fi-chat-conversation-helper.service';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';

@Component({
    selector: 'fi-chat-conversation-existing',
    templateUrl: 'fi-chat-conversation-existing.component.html',
    styleUrls: ['./fi-chat-conversation-existing.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-chat-conversation' },
    standalone: false,
})
export class FiChatConversationExistingComponent
    extends FiDataSourceBase<IChatConversationMessage>
    implements OnInit, OnDestroy
{
    @Input() conversation: IChatConversation;
    @Input() isMessageInputFocus: boolean;
    @Input() newMediaBankReferences$ = new Observable<IMediaBankFileReference[]>();
    @Input() isMessageInputShown: boolean = true;

    @ViewChild('messagesWrapperRef', { static: false }) messagesWrapperRef: ElementRef<HTMLElement>;

    private fiChatService = inject(FiChatService);
    private fiChatHelperService = inject(FiChatHelperService);
    private fiChatSyncService = inject(FiChatSyncService);
    private userDataService = inject(FiUserDataService);
    private fiEmojiService = inject(FiEmojiService);
    private fiSignalRService = inject(FiSignalRService);
    private appTextService = inject(AppTextService);
    private mobileAppHelper = inject(MobileAppHelperBaseService);
    private whenScrolledService = inject(WhenScrolledService);
    private cdr = inject(ChangeDetectorRef);
    private scrollToElementAnimateService = inject(FiScrollToElementAnimateService);
    private fiChatConversationMessageActionsService = inject(FiChatConversationMessageActionsService);
    private fiChatConversationHelperService = inject(FiChatConversationHelperService);
    private fiBrowserEventsService = inject(FiBrowserEventsService);
    private destroyRef = inject(DestroyRef);
    protected elementRef = inject(ElementRef);

    protected isContentShown = signal<boolean>(false);
    protected newConversationMessage: IChatConversationMessage =
        this.fiChatHelperService.getNewConversationMessageInstance();
    protected isEditMode: boolean = false;
    protected editableMessage: IChatConversationMessage;
    protected conversationUsersTypingData$ = new BehaviorSubject<IChatConversationIsTypingChangedEventData[]>([]);
    protected sourceMessage: IChatConversationMessage;
    protected hideMessageInput: boolean;
    protected userDeletedMessage: string;

    private typingTimer: NodeJS.Timeout;
    private isTyping = false;
    private isDataReloading = false;

    private readonly typingTimerValue = 5000;
    private readonly scrollToSourceMessageHighlightClass: string = 'reply-highlight';
    private readonly scrollToSourceMessageHighlightTimeMS: number = 2000;

    constructor(injector: Injector) {
        super(injector, { reverseMode: false });
    }

    private get conversationUserIds(): number[] {
        return this.conversation.Users.map((u) => u.Id);
    }

    private _isEmojiMartReactionOpenedForMessage = signal<number | null>(null);
    protected toggleReactionEmojiMartVisibility(messageId: number): void {
        this._isEmojiMartReactionOpenedForMessage.update((currentOpen) =>
            currentOpen === messageId ? null : messageId,
        );
    }
    protected isEmojiMartReactionPanelOpen(messageId: number): boolean {
        return this._isEmojiMartReactionOpenedForMessage() === messageId;
    }

    private subscribeOnClickOutside(): void {
        this.fiBrowserEventsService
            .getDocumentEventSubject$('click')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(
                    (event) =>
                        !eventFindInPath(event, '.fi-preferred-emojis-menu ') &&
                        !eventFindInPath(event, '.fi-emoji-mart-overlay '),
                ),
            )
            .subscribe(() => {
                this._isEmojiMartReactionOpenedForMessage.set(null);
            });
    }

    public ngOnInit(): void {
        this.setDeletedUserMessage();
        this.reloadData().then(() => {
            this.isContentShown.set(true); // for proper animating on opening conversation
        });
        this.subscribeOnConversationUpdate();
        this.subscribeOnTypingChange();
        this.setOnMediaBankReferenceDropSubscription();
        this.subscribeOnForwardMessage();
        this.subscribeOnConversationMessageActions();
        this.subscribeOnClickOutside();
    }

    public loadItemsFn(loadArgs: ILoadDataArgs): IFiHttpPromise<IChatConversationMessage[]> {
        const promise = this.fiChatService.getConversationMessages(
            this.conversation.Id,
            loadArgs.fetchParams.offset,
            loadArgs.fetchParams.numberOfRecordsToTake,
        );
        promise.then(() => {
            // https://chainformation.atlassian.net/browse/FI-4664
            // blank content in iOS mobile app after loading next data fix
            if (!this.mobileAppHelper.isMobileAppIos()) {
                return;
            }
            setTimeout(() => {
                const wrapper = this.messagesWrapperRef?.nativeElement;
                if (!wrapper?.scrollTop) {
                    return;
                }
                this.whenScrolledService.withIgnoringScrollEvent((resolve) => {
                    wrapper.scrollTop -= 1;
                    resolve();
                });
            }, 1);
        });
        return promise;
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
        this.removeTypingStatus();
    }

    protected scrollToMessage(
        message: IChatConversationMessage,
        className = 'message-highlighted',
        highlightTime = 500,
    ) {
        this.fiChatConversationHelperService.scrollToMessage(
            message,
            this.messagesWrapperRef.nativeElement,
            className,
            highlightTime,
        );
    }

    protected getConversationMessageWrapperId(conversationMessage: IChatConversationMessage): string {
        return this.fiChatConversationHelperService.getConversationMessageWrapperId(conversationMessage.Id);
    }

    protected updateEmojiPickerPosition(): void {
        this.fiEmojiService.updateEmojiOverlayPosition();
    }

    protected saveMessage(messageToSave: IChatConversationMessage): void {
        this.removeTypingStatus();
        if (isDefined(this.sourceMessage)) {
            messageToSave.SourceMessage = this.sourceMessage;
        }

        this.fiChatService.saveConversationMessage(this.conversation.Id, messageToSave).then(() => {
            // when sending gifs, we don't need to reset message
            // gifs are sent as separate messages
            if (!messageToSave.Links.length) {
                this.newConversationMessage = this.fiChatHelperService.getNewConversationMessageInstance();
            }
            if (this.isEditMode) {
                this.cancelEditMode();
            }
        });
    }

    protected onScrollToBottom(): void {
        this.loadNext();
    }

    protected cancelEditMode(): void {
        this.isEditMode = false;
        this.editableMessage = undefined;
        this.sourceMessage = undefined;
    }

    protected navigateToSourceMessage(message: IChatConversationMessage, skipMessage: IChatConversationMessage): void {
        this.loadMessagesToMessage(message, skipMessage).then(() => {
            setTimeout(() => {
                this.scrollToMessage(
                    this.getSourceMessageToScrollTo(message, skipMessage),
                    this.scrollToSourceMessageHighlightClass,
                    this.scrollToSourceMessageHighlightTimeMS,
                );
            });
        });
    }

    protected reloadData(): Promise<boolean> {
        this.isDataReloading = true;
        const promise: Promise<boolean> = super.reloadData();
        promise
            .then((result) => {
                if (result) {
                    this.markConversationAsVisited();
                }
            })
            .finally(() => (this.isDataReloading = false));
        return promise;
    }

    protected setTypingStatus(): void {
        if (!this.isTyping) {
            this.fiChatService.triggerIsConversationTypingChanged(this.conversation.Id, true, this.conversationUserIds);
            this.isTyping = true;
        }

        clearTimeout(this.typingTimer);
        this.typingTimer = setTimeout(() => this.removeTypingStatus(), this.typingTimerValue);
    }

    protected isMessageDateShown(message: IChatConversationMessage): boolean {
        return this.fiChatConversationHelperService.isMessageDateShown(message, this.items());
    }

    protected isCreatedByUserNameShown(message: IChatConversationMessage): boolean {
        return this.fiChatConversationHelperService.isCreatedByUserNameShown(message, this.items());
    }

    protected isUserProfileImageShown(message: IChatConversationMessage): boolean {
        return this.fiChatConversationHelperService.isUserProfileImageShown(message, this.items());
    }

    private getSourceMessageToScrollTo(
        message: IChatConversationMessage,
        skipMessage: IChatConversationMessage,
    ): IChatConversationMessage {
        return this.fiChatConversationHelperService.getSourceMessageToScrollTo(message, this.items(), skipMessage);
    }

    private async loadMessagesToMessage(
        message: IChatConversationMessage,
        skipMessage: IChatConversationMessage,
    ): Promise<boolean> {
        if (this.getSourceMessageToScrollTo(message, skipMessage)) {
            return true;
        }
        await this.loadNext();
        return this.loadMessagesToMessage(message, skipMessage);
    }

    private setDeletedUserMessage(): void {
        // https://chainformation.atlassian.net/browse/FI-6542
        // check if message input should be hidden and 'user deleted' message should be shown
        const otherUsers = this.conversation.Users.filter(
            (conversationUser) => !this.fiChatHelperService.isCurrentUser(conversationUser.Id),
        );
        const deletedUsers = this.conversation.Users.filter((conversationUser) => conversationUser.IsDeleted);
        const allUsersDeleted = otherUsers.length === deletedUsers.length;

        // https://chainformation.atlassian.net/browse/FI-6611
        // show edit message input in group chats even if all other users were deleted/left the group
        this.hideMessageInput =
            !this.isMessageInputShown || (allUsersDeleted && this.conversation.Type !== FiChatConversationType.Group);

        if (deletedUsers.length > 0) {
            if (this.conversation.Type === FiChatConversationType.OneToOne) {
                this.userDeletedMessage = this.appTextService.get('chat.user_deleted_one_to_one');
            } /* else if (this.conversation.Type == FiChatConversationType.Group) {
                this.userDeletedMessage = this.appTextService.get('chat.user_deleted_group', { '{user_name}': deletedUsers[0].FullName });
            }*/
        }
    }

    private subscribeOnConversationUpdate(): void {
        this.fiChatSyncService
            .conversationUpdated$(this.conversation.Id)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(($event) => {
                if ($event.forceReload) {
                    // chat conversation out of sync with the server so have to force reload data
                    this.reloadData();
                }
            });

        // subscribe on message updated event (from current user or received via signalR)
        this.fiChatSyncService.conversationMessageUpdated$
            .pipe(
                filter((item) => item.ConversationId === this.conversation.Id && !this.isDataReloading),
                takeUntil(this.ngOnDestroy$),
            )
            .subscribe((updatedMessage) => {
                this.updateOrAddConversationMessage(updatedMessage);

                if (this.fiChatHelperService.isConversationListOpened()) {
                    // mark chat as visited after 1 sec when chat window is opened
                    setTimeout(() => {
                        this.markConversationAsVisited();
                    }, 1000);
                } else {
                    this.fiChatHelperService.showConversationList$
                        .pipe(
                            takeUntil(this.ngOnDestroy$),
                            filter((v) => v),
                            take(1),
                        )
                        .subscribe(() => {
                            this.markConversationAsVisited();
                        });
                }
            });
    }

    private updateOrAddConversationMessage(message: IChatConversationMessage): void {
        const messages = this.items();
        const existingMessageIdx = messages.findIndex((x) => x.Id === message.Id);
        if (existingMessageIdx !== -1) {
            messages[existingMessageIdx] = message;
        } else {
            messages.unshift(message);
            if (this.isScrollToBottom(message.CreatedByUserId)) {
                this.scrollToBottomOnMessageSent();
            }
        }
        this.setItems(messages);
    }

    private isScrollToBottom(userId: number): boolean {
        return userId === this.userDataService.getUserId() || this.mobileAppHelper.isMobileAppIos();
    }

    private scrollToBottomOnMessageSent(): void {
        // https://chainformation.atlassian.net/browse/FI-4661
        if (this.mobileAppHelper.isMobileAppIos()) {
            setTimeout(() => {
                this.whenScrolledService.withIgnoringScrollEvent((resolve) => {
                    this.messagesWrapperRef.nativeElement.scrollTop = -1;
                    setTimeout(() => {
                        this.messagesWrapperRef.nativeElement.scrollTop = 0;
                        resolve();
                    }, 500);
                });
            }, 10);
        } else {
            setTimeout(() => {
                if (this.messagesWrapperRef) {
                    this.scrollToElementAnimateService.scrollToY(this.messagesWrapperRef.nativeElement, 0);
                }
            });
        }
    }

    private subscribeOnTypingChange(): void {
        this.fiSignalRService.chatConversationIsTypingChangedEvent.subscribe(
            (data: IChatConversationIsTypingChangedEventData) => {
                if (data.ConversationId === this.conversation.Id) {
                    this.updateUsersTypingData(data);
                }
            },
            this.ngOnDestroy$,
        );
    }

    private subscribeOnConversationMessageActions(): void {
        const actions = [
            {
                stream$: this.fiChatConversationMessageActionsService.conversationMessageEdit$,
                handler: (data: IChatConversationMessageActionsData) =>
                    this.onConversationMessageEditClick(data.conversationMessage),
            },
            {
                stream$: this.fiChatConversationMessageActionsService.conversationMessageDelete$,
                handler: (data: IChatConversationMessageActionsData) =>
                    this.onConversationMessageDeleteClick(data.conversationMessage),
            },
            {
                stream$: this.fiChatConversationMessageActionsService.conversationMessageDeleteForUser$,
                handler: (data: IChatConversationMessageActionsData) =>
                    this.onConversationMessageDeleteForYouClick(data.conversationMessage),
            },
            {
                stream$: this.fiChatConversationMessageActionsService.conversationMessageReplyEdit$,
                handler: (data: IChatConversationMessageActionsData) =>
                    this.onConversationMessageReplyClick(data.conversationMessage),
            },
            {
                stream$: this.fiChatConversationMessageActionsService.conversationMessageForward$,
                handler: (data: IChatConversationMessageActionsData) =>
                    this.onConversationMessageForwardClick(data.conversationMessage),
            },
        ];

        actions.forEach(({ stream$, handler }) => {
            stream$
                .pipe(
                    takeUntil(this.ngOnDestroy$),
                    filter(
                        (actionData: IChatConversationMessageActionsData) =>
                            actionData.conversationId === this.conversation.Id,
                    ),
                )
                .subscribe(handler);
        });
    }

    private updateUsersTypingData(isTypingEvent: IChatConversationIsTypingChangedEventData): void {
        const typingData: IChatConversationIsTypingChangedEventData[] = this.conversationUsersTypingData$.value;
        const displayedMessageIndex: number = typingData.findIndex(
            (data: IChatConversationIsTypingChangedEventData) => {
                return data.UserId === isTypingEvent.UserId;
            },
        );

        if (!isTypingEvent.IsTyping && displayedMessageIndex !== -1) {
            typingData.splice(displayedMessageIndex, 1);
            this.conversationUsersTypingData$.next([...typingData]);
            return;
        }
        if (isTypingEvent.IsTyping && displayedMessageIndex === -1) {
            typingData.unshift(isTypingEvent);
            this.conversationUsersTypingData$.next([...typingData]);
            return;
        }
    }

    private setOnMediaBankReferenceDropSubscription(): void {
        this.newMediaBankReferences$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((mediaBankFileReferences) => {
            this.newConversationMessage.MediaBankFileReferences = [
                ...this.newConversationMessage.MediaBankFileReferences,
                ...mediaBankFileReferences,
            ];
            this.newConversationMessage = { ...this.newConversationMessage };
            this.cdr.markForCheck();
            this.setTypingStatus();
        });
    }

    private subscribeOnForwardMessage(): void {
        this.fiChatHelperService.forwardMessage$
            .pipe(
                takeUntil(this.ngOnDestroy$),
                filter((messageData) => {
                    return isDefined(messageData) && this.conversation.Id === messageData.targetConversationId;
                }),
            )
            .subscribe((messageData) => {
                this.handleConversationMessageAction(messageData.message, FiChatSourceMessageType.Forwarded);
                this.cdr.markForCheck();
            });
    }

    private removeTypingStatus(): void {
        clearTimeout(this.typingTimer);
        this.fiChatService.triggerIsConversationTypingChanged(this.conversation.Id, false, this.conversationUserIds);
        this.isTyping = false;
    }

    private onConversationMessageDeleteForYouClick(deleteMessage: IChatConversationMessage): void {
        this.fiChatConversationHelperService.getConfirmDeleteMessageDialog(true).onYesButtonClick(() => {
            this.fiChatService.deleteConversationMessageForUser(this.conversation.Id, deleteMessage.Id).then(() => {
                const messages = this.items();
                const existingMessage = messages.find((x) => x.Id === deleteMessage.Id);
                if (existingMessage) {
                    existingMessage.IsHidden = true;
                    this.setItems(messages);
                }
            });
        });
    }

    private onConversationMessageDeleteClick(deleteMessage: IChatConversationMessage): void {
        this.fiChatConversationHelperService.getConfirmDeleteMessageDialog(false).onYesButtonClick(() => {
            this.fiChatService.deleteConversationMessages(this.conversation.Id, [deleteMessage.Id]).then(() => {
                const messages = this.items();
                const existingMessage = messages.find((x) => x.Id === deleteMessage.Id);
                if (existingMessage) {
                    existingMessage.Type = FiChatConversationMessageType.Deleted;
                    const existingSourceMessage = messages.find((message) => {
                        return message.SourceMessageId === deleteMessage.Id;
                    })?.SourceMessage;
                    if (existingSourceMessage) {
                        existingSourceMessage.Type = FiChatConversationMessageType.Deleted;
                    }
                    this.setItems(messages);
                }
            });
        });

        return;
    }

    private onConversationMessageEditClick(conversationMessage: IChatConversationMessage): void {
        this.isEditMode = true;
        this.editableMessage = cloneDeep(conversationMessage);
    }

    private onConversationMessageForwardClick(conversationMessage: IChatConversationMessage): void {
        this.fiChatHelperService.openForwardMessageConversationList(conversationMessage);
    }

    private onConversationMessageReplyClick(conversationMessage: IChatConversationMessage): void {
        this.handleConversationMessageAction(conversationMessage, FiChatSourceMessageType.Reply);
    }

    private handleConversationMessageAction(
        conversationMessage: IChatConversationMessage,
        sourceMessageType: FiChatSourceMessageType,
    ): void {
        const currentMessage = this.fiChatHelperService.isOnlySourceMessage(conversationMessage)
            ? conversationMessage.SourceMessage
            : conversationMessage;
        if (currentMessage.Type === FiChatConversationMessageType.Deleted) return;

        this.isEditMode = true;
        this.sourceMessage = cloneDeep(currentMessage);
        this.editableMessage = Object.assign({}, currentMessage);
        this.editableMessage.SourceMessageId = this.sourceMessage.Id;
        this.editableMessage.SourceMessageType = sourceMessageType;
        this.editableMessage.Id = undefined;
        this.editableMessage.Content = '';
        this.editableMessage.MediaBankFileReferences = [];
        this.editableMessage.Links = [];
        this.editableMessage.ReadBy = [];
    }

    private markConversationAsVisited(): void {
        const shouldMarkConversationAsVisited = this.fiChatConversationHelperService.shouldMarkConversationAsVisited(
            this.conversation.NotificationCategory,
            this.items(),
        );
        if (shouldMarkConversationAsVisited) {
            this.fiChatService.markConversationAsVisited(this.conversation.Id).then((messageIds) => {
                this.updateMessagesAsVisited(messageIds);
            });
        }
    }

    private updateMessagesAsVisited(messageIds: number[]): void {
        const messagesToUpdate = this.items().filter((message) => messageIds.includes(message.Id));
        messagesToUpdate.forEach((message) => {
            message.NotificationCategory = PersonalNotificationsCategoryType.Visited;
        });
    }
}
